import { ColDef, ColumnState } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef, useState } from "react";

import {
	aggridHeaderClass,
	commonBorderStyle,
	commonNumberCellStyle,
	commonTextCellStyle,
} from "src/components/common/common";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import Grid_Touch from "src/components/common/gridWrapper/Grid_Touch";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { defaultColDef, getDefaultRowIndexColumnConfig } from "src/utils/grid/gridStyleUtils";
import { defaultItem } from "src/dtos/item/itemDto";

import { SAL2100_R01BrandDto } from "../Dto_SAL2100_R01";

type BrandGridRow = SAL2100_R01BrandDto & { inserted?: boolean };

const formatNumber = (value: number | null | undefined) =>
	Number(value ?? 0).toLocaleString("ko-KR", { maximumFractionDigits: 2 });

export const BrandTable_SAL2100_R01 = ({ data }: { data: SAL2100_R01BrandDto[] }) => {
	const gridRef = useRef<AgGridReact<BrandGridRow>>(null);
	const [, setHoveredRowIndex] = useState<number | null>(null);
	const [, setColumnState] = useState<ColumnState[]>([]);
	const [scrollTop, setScrollTop] = useState<number | null>(null);
	const rowData = useMemo<BrandGridRow[]>(() => data.map((item) => ({ ...item })), [data]);
	const totalSales = data.reduce((sum, item) => sum + item.salesAmount, 0);
	const columnDefs = useMemo<ColDef<BrandGridRow>[]>(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: "브랜드",
			field: "brandNm",
			colId: "brandNm",
			flex: 1.5,
			minWidth: 110,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonTextCellStyle, textAlign: "center" }),
			valueGetter: (params) => params.data?.brandNm || params.data?.brandCd || "미지정",
		},
		{
			headerName: "매출액",
			field: "salesAmount",
			colId: "salesAmount",
			flex: 1.5,
			minWidth: 110,
			headerClass: aggridHeaderClass,
			cellStyle: () => ({ ...commonBorderStyle, ...commonNumberCellStyle }),
			valueFormatter: (params) => formatNumber(params.value),
		},
	], []);
	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(columnDefs, []),
		[columnDefs],
	);
	const totalRow = useMemo<BrandGridRow[]>(() => [{
		...defaultItem,
		brandNm: "합계",
		salesAmount: totalSales,
		shareRate: 0,
	}], [totalSales]);

	return (
		<GridContextWrapper<BrandGridRow>
			gridRef={gridRef}
			paddedGridData={rowData}
			initSelected={() => {}}
			defaultRowData={{ ...defaultItem, brandNm: "", salesAmount: 0, shareRate: 0 }}
			baseColumnDefs={columnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={(state) => setColumnState(state ?? [])}
			localStorageKey="SAL2100_R01-brand-grid-column-state"
			menuItems={undefined}
			enableDataHistory={false}
		>
			<Grid_Touch<BrandGridRow>
				ref={gridRef}
				rowData={rowData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				domLayout="autoHeight"
				getRowId={(params) => params.data.brandCd || params.data.brandNm}
				onCellKeyDown={undefined}
				pinnedBottomRowData={totalRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey="SAL2100_R01-brand-grid-column-state"
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				enableCellTextSelection
			/>
		</GridContextWrapper>
	);
};
